import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import {
  ClipboardList, Users, KeyRound, Printer, Share2, Send, Activity,
  RefreshCw, Sparkles, Shield, ArrowRight, CheckCircle2, AlertTriangle, HelpCircle, BookOpen,
} from "lucide-react";

const Step = ({ n, icon: Icon, title, testid, children }) => (
  <div className="stage-card p-6" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-mm-navy text-white flex items-center justify-center font-heading font-black text-xl">{n}</div>
      <div className="w-10 h-10 rounded-xl bg-mm-steel/15 text-mm-navy flex items-center justify-center">
        <Icon size={20} strokeWidth={2.5} />
      </div>
      <h3 className="font-heading text-xl sm:text-2xl font-black text-mm-navy">{title}</h3>
    </div>
    <div className="mt-4 text-mm-navy/85 text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

const Tip = ({ tone = "info", children }) => {
  const styles = {
    info: "border-mm-steel/30 bg-mm-steel/5 text-mm-navy",
    warn: "border-mm-gold/50 bg-mm-gold/10 text-mm-navy",
    safe: "border-emerald-300 bg-emerald-50 text-emerald-900",
  }[tone];
  const Icon = tone === "warn" ? AlertTriangle : tone === "safe" ? Shield : HelpCircle;
  return (
    <div className={`rounded-lg border-2 ${styles} p-3 flex items-start gap-2`}>
      <Icon size={16} strokeWidth={2.5} className="mt-0.5 shrink-0" />
      <div className="text-sm">{children}</div>
    </div>
  );
};

const TeacherSetupGuide = () => {
  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto max-w-4xl py-10 px-4">
        <Link to="/teacher/dashboard" className="text-sm text-mm-steel font-bold hover:text-mm-navy" data-testid="back-to-hq-link">← Back to HQ</Link>

        {/* Hero */}
        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail Leader HQ</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-mm-navy mt-2" data-testid="teacher-setup-title">
            Set up your class in 10 minutes
          </h1>
          <p className="text-mm-navy/75 mt-3 text-base max-w-2xl">
            This guide walks you through everything you need to do to get your students climbing.
            No tech skills required — if you can send an email, you can run a Mind Map class.
          </p>
        </div>

        {/* Quick jump */}
        <div className="mt-6 rounded-2xl bg-white border-2 border-mm-navy/10 p-5" data-testid="setup-toc">
          <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">What you'll do</div>
          <ol className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-mm-navy list-decimal pl-5">
            <li>Create a class</li>
            <li>Understand your 30 student slots</li>
            <li>See or print the login PINs</li>
            <li>Share the login instructions with students</li>
            <li>Assign private nicknames (optional but helpful)</li>
            <li>Push your first expedition to the whole class</li>
            <li>Watch students in real time with Live Monitor</li>
            <li>Reset a PIN when someone forgets theirs</li>
            <li>Print a roster for records</li>
            <li>Add more than 30 students (upgrade info)</li>
          </ol>
        </div>

        {/* Overview */}
        <div className="mt-6 rounded-2xl bg-mm-navy text-white p-6" data-testid="setup-overview">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-mm-gold">
            <Sparkles size={14} strokeWidth={2.5} /> Before you start — the big picture
          </div>
          <p className="mt-3 text-white/90 leading-relaxed">
            Every class you create comes with <b>30 anonymous student accounts</b> ready to go — no email addresses,
            no last names, no personal info required. Each student gets a fun explorer <b>username</b>
            (like <span className="font-mono">Skyward2246</span>) and a secret <b>4-digit PIN</b>. That's it.
            You keep a private list of who's who; the app never sees a real name.
          </p>
        </div>

        {/* Steps */}
        <div className="mt-8 space-y-5">

          <Step n={1} icon={ClipboardList} title="Create your first class" testid="setup-step-1">
            <ol className="list-decimal pl-5 space-y-1.5">
              <li>From the Teacher HQ page, click <b>New class</b> in the top right corner.</li>
              <li>Give your class a name you'll recognize — like <span className="font-mono">Period 3 Science</span> or <span className="font-mono">Homeroom — Mrs. Diaz</span>.</li>
              <li>Add the subject (like <span className="font-mono">Life Science</span>) and grade level (like <span className="font-mono">5</span>). Both are optional but make sorting easier later.</li>
              <li>Click <b>Create class</b>. Mind Map instantly builds 30 anonymous student accounts for that class.</li>
            </ol>
            <Tip tone="info">
              You can create <b>as many classes as you need</b> after upgrading to Teacher Premium ($100 for the whole school year).
              On the free trial you can test out one class with up to 30 students.
            </Tip>
          </Step>

          <Step n={2} icon={Users} title="What are the 30 “slots”?" testid="setup-step-2">
            <p>
              Think of a slot like an empty desk with a name tag. You have 30 desks — one per student.
              Each slot already has a random <b>username</b> (like <span className="font-mono">Explorer1234</span>) and a <b>4-digit PIN</b>.
              You'll match your real students to these slots when you hand out the login cards.
            </p>
            <p>
              Your class roster page shows all 30 slots in a table. You can see:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Slot number</b> — 1 through 30.</li>
              <li><b>Username</b> — the fun name the student will type to log in.</li>
              <li><b>PIN</b> — the 4-digit code the student types to log in. It's always shown to you, and it never changes unless you change it.</li>
              <li><b>Assigned to</b> — a private nickname you type (like <span className="font-mono">Front row left</span> or a student's real first name). Only you see this.</li>
              <li><b>Last active</b> — when that student last used their account.</li>
            </ul>
          </Step>

          <Step n={3} icon={KeyRound} title="See or print the login PINs" testid="setup-step-3">
            <p>You have three easy options for getting PINs to your students. Pick the one that fits your classroom.</p>

            <div className="rounded-xl border-2 border-mm-navy/10 bg-white p-4">
              <div className="font-heading font-black text-mm-navy flex items-center gap-2"><Printer size={16} strokeWidth={2.5} /> Option A — Print Login Cards (recommended)</div>
              <ol className="list-decimal pl-5 mt-2 space-y-1">
                <li>Open your class roster page.</li>
                <li>Click <b>Print Login Cards</b>. A PDF downloads with one small card per student — username + PIN + login website.</li>
                <li>Print the PDF, cut along the lines, and hand each student a card.</li>
              </ol>
            </div>

            <div className="rounded-xl border-2 border-mm-navy/10 bg-white p-4">
              <div className="font-heading font-black text-mm-navy flex items-center gap-2"><Users size={16} strokeWidth={2.5} /> Option B — Read PINs on screen</div>
              <ol className="list-decimal pl-5 mt-2 space-y-1">
                <li>Open your class roster page.</li>
                <li>Every PIN is always shown in the PIN column as a gold box. Click any PIN to copy it.</li>
                <li>Read the PIN aloud to that student, or write it on their card.</li>
              </ol>
            </div>

            <div className="rounded-xl border-2 border-mm-navy/10 bg-white p-4">
              <div className="font-heading font-black text-mm-navy flex items-center gap-2"><Printer size={16} strokeWidth={2.5} /> Option C — Print the whole Roster</div>
              <p className="mt-2">Click <b>Print Roster</b> for a single-page PDF with every username and PIN in one list. Great for your grade book or a substitute teacher.</p>
            </div>

            <Tip tone="info">
              <b>Good to know:</b> PINs stay the same. You can print cards or the roster as many times as you want —
              the PINs won't change. If you ever want everyone to get new PINs, click <b>Generate New PINs</b> on the
              roster page. That changes every PIN at once, and the old PINs stop working.
            </Tip>
          </Step>

          <Step n={4} icon={Share2} title="Show students how to log in" testid="setup-step-4">
            <p>
              We built a kid-friendly page that walks students through logging in step by step,
              with pictures and examples. You can share the link, put it on your class website,
              print it, or project it on the board.
            </p>
            <div className="rounded-xl bg-mm-parchment border-2 border-mm-navy/10 p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">The link to share</div>
              <div className="mt-1 font-mono font-bold text-mm-navy break-all">{typeof window !== "undefined" ? `${window.location.origin}/student/login-guide` : "/student/login-guide"}</div>
              <Link to="/student/login-guide" target="_blank" rel="noopener" className="btn-trail btn-outline-navy text-xs mt-3" data-testid="open-student-guide-btn">
                <BookOpen size={12} strokeWidth={2.5} /> Open the guide in a new tab
              </Link>
            </div>
            <p>The student guide covers three simple steps: go to the login page, type your username, enter your 4-digit PIN.</p>
          </Step>

          <Step n={5} icon={Users} title="Assign private nicknames (optional)" testid="setup-step-5">
            <p>
              Because usernames are random (<span className="font-mono">Skyward2246</span>), it can be hard to remember
              which slot belongs to which student. To fix that, you can add a <b>private nickname</b> that
              <b> only you see</b>. The student never sees this nickname, and neither does the app's AI.
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>On the roster, find the slot you want to label.</li>
              <li>Click the pencil icon in the <b>Assigned to</b> column.</li>
              <li>Type anything you'd recognize — a first name (<span className="font-mono">Maria</span>), a seat (<span className="font-mono">Front row 3</span>), or a group (<span className="font-mono">Blue team</span>).</li>
              <li>Click the green checkmark to save.</li>
            </ol>
            <Tip tone="safe">
              Nicknames are stored only on your teacher account — never on the student's record.
              This keeps the class fully COPPA-compliant.
            </Tip>
          </Step>

          <Step n={6} icon={Send} title="Push your first expedition to the class" testid="setup-step-6">
            <p>
              An <b>expedition</b> is Mind Map's word for a lesson. It has 5 stages (Base Camp → Trail 1 → Trail 2 → Trail 3 → Summit),
              and each stage includes vocabulary flashcards, a matching activity, and a short quiz. The AI writes it in seconds.
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>On the roster page, click <b>Push expedition</b> in the top right.</li>
              <li>Type a topic — anything you'd cover in class (like <span className="font-mono">Photosynthesis</span>, <span className="font-mono">Fractions</span>, or <span className="font-mono">The Water Cycle</span>).</li>
              <li>Pick a difficulty:
                <ul className="list-disc pl-5 mt-1">
                  <li><b>Standard trail</b> — grade-level content for the whole class.</li>
                  <li><b>Rescue route</b> — extra scaffolding for students who need more support.</li>
                  <li><b>Explorer's challenge</b> — deeper questions for advanced students.</li>
                </ul>
              </li>
              <li>Click <b>Push to class</b>. Every <b>assigned</b> student (with a nickname) gets that expedition on their dashboard immediately. Unassigned slots are skipped.</li>
            </ol>
            <Tip tone="info">
              Want to send a different expedition to different groups? Push a standard one to your Blue team,
              then push a Rescue Route version to your Red team. There's no cap on how many you can push.
            </Tip>
          </Step>

          <Step n={7} icon={Activity} title="Watch students in real time with Live Monitor" testid="setup-step-7">
            <p>
              While students are working, click <b>Live monitor</b> from your class roster. You'll see:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Who is <b>active right now</b> (green dot — last activity under 5 minutes ago).</li>
              <li>Who was <b>recently active</b> (yellow dot — 5 to 30 minutes ago).</li>
              <li>Who is <b>idle</b> (gray dot — more than 30 minutes).</li>
              <li>What expedition each student is on, and how many stages they've finished.</li>
            </ul>
            <p>Great for spotting who's stuck, who's flying, and who needs a nudge.</p>
          </Step>

          <Step n={8} icon={RefreshCw} title="Reset a PIN when someone forgets theirs" testid="setup-step-8">
            <p>
              Kids forget PINs — it happens every day. Reset just that one student in seconds:
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Find the student's row on the roster.</li>
              <li>Click the circular refresh icon in the <b>Actions</b> column.</li>
              <li>Confirm. A brand-new 4-digit PIN appears right in the PIN column and in a green message at the top of the page.</li>
              <li>Write the new PIN on their login card, or read it to them.</li>
            </ol>
            <Tip tone="info">
              This only resets that one student's PIN. Everyone else's PIN stays the same.
            </Tip>
          </Step>

          <Step n={9} icon={Printer} title="Print a roster for your records" testid="setup-step-9">
            <p>
              Need a paper backup or something to share with an administrator?
              Click <b>Print Roster</b> — you'll get a clean, single-page PDF listing every username and PIN in your class.
            </p>
            <p className="text-mm-navy/70">Printing never changes PINs, so you can print a fresh copy anytime.</p>
          </Step>

          <Step n={10} icon={Sparkles} title="Add more than 30 students" testid="setup-step-10">
            <p>
              The free plan supports one class of 30. If you teach multiple periods or a bigger group,
              <b> Teacher Premium</b> is <b>$100 for the whole school year</b> and unlocks:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Unlimited classes.</li>
              <li>Unlimited expedition pushes.</li>
              <li>Live Monitor for every class.</li>
              <li>All future teacher features at no extra cost this year.</li>
            </ul>
            <ol className="list-decimal pl-5 space-y-1 mt-2">
              <li>From your Teacher HQ page, click <b>Unlock — $100/yr</b> at the top.</li>
              <li>Enter payment on the secure Stripe checkout screen.</li>
              <li>You're taken straight back to HQ, unlocked and ready to create more classes.</li>
            </ol>
            <Tip tone="safe">
              Schools that need to pay by <b>Purchase Order</b> (PO) can request one from the
              {" "}<Link to="/schools/purchase-order" className="underline font-bold" data-testid="po-link">Schools Purchase Order</Link> page.
              We'll email your business office an invoice and unlock accounts once it's approved.
            </Tip>
          </Step>

        </div>

        {/* FAQ / Privacy quick answers */}
        <div className="mt-10 rounded-2xl bg-white border-2 border-mm-navy/10 p-6" data-testid="setup-faq">
          <h3 className="font-heading text-2xl font-black text-mm-navy flex items-center gap-2">
            <Shield size={20} strokeWidth={2.5} /> Quick answers
          </h3>
          <div className="mt-4 space-y-4 text-sm text-mm-navy/85">
            <div>
              <div className="font-bold text-mm-navy">Do students need email addresses?</div>
              <p>No. Student accounts are fully anonymous — no name, no email, no personal data collected. They log in with a username + PIN only.</p>
            </div>
            <div>
              <div className="font-bold text-mm-navy">Is this COPPA compliant?</div>
              <p>Yes. Because student accounts collect zero personal information, Mind Map's classroom mode is designed to comply with COPPA for K-12 use.</p>
            </div>
            <div>
              <div className="font-bold text-mm-navy">What if a student sees another student's PIN?</div>
              <p>Reset that student's PIN in one click from the roster page (Step 8 above). Old PIN stops working immediately.</p>
            </div>
            <div>
              <div className="font-bold text-mm-navy">Can I use this on Chromebooks / iPads?</div>
              <p>Yes. Any modern web browser works — Chrome, Safari, Edge, Firefox. Mobile phones work too.</p>
            </div>
            <div>
              <div className="font-bold text-mm-navy">Can I delete a class?</div>
              <p>Not yet from the UI — email us and we'll clear it for you. Because there's no student personal data, you can simply stop using a class and its slots stay anonymous.</p>
            </div>
          </div>
        </div>

        {/* Ready to go */}
        <div className="mt-10 rounded-2xl bg-mm-gold/15 border-2 border-mm-gold/50 p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="setup-ready-cta">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-mm-gold text-mm-navy flex items-center justify-center">
              <CheckCircle2 size={22} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading text-2xl font-black text-mm-navy">You're ready to lead a trail.</div>
              <p className="text-sm text-mm-navy/80 mt-1">Head back to Trail Leader HQ and create your first class.</p>
            </div>
          </div>
          <Link to="/teacher/dashboard" className="btn-trail btn-gold text-sm shrink-0" data-testid="setup-go-hq-btn">
            Go to HQ <ArrowRight size={14} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TeacherSetupGuide;
